// Foto aufnehmen über <input type="file">. Das ist auf iPhone (Safari) und
// Android ohne App und ohne Kamera-Berechtigungsdialog am zuverlässigsten.
// "capture" öffnet direkt die Kamera; die Galerie erlaubt mehrere Fotos.

import { useEffect, useId, useRef, useState } from "react";
import { HALL_PHOTO_STEPS, type PhotoStep } from "@umzugsscan/shared";
import { processImage } from "../bild.ts";
import { useDraft, useStore } from "../hooks.ts";

export function Kamera(props: {
  roomId: string;
  step: PhotoStep;
  /** Abschnitt des Flurs (14.10.2026), nur bei Flurfotos. */
  section?: number;
  label?: string;
  single?: boolean;
  onAdded?: () => void;
  /** Meldet, ob gerade Fotos verarbeitet werden (dann kein "Weiter"). */
  onBusy?: (busy: boolean) => void;
  /** Hinweis "Kamera auf 0,5×" (Standard: an; aus, wenn zwei Kameras untereinander stehen). */
  zoomHint?: boolean;
  /** Kachel in einer Fotoreihe (30.10.2026, Flurfotos je Wand nebeneinander statt untereinander). */
  kompakt?: boolean;
}) {
  const store = useStore();
  const id = useId();
  const camera = useRef<HTMLInputElement>(null);
  const gallery = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const d = useDraft();
  const { onBusy } = props;
  useEffect(() => {
    onBusy?.(busy > 0);
  }, [busy, onBusy]);
  useEffect(() => () => onBusy?.(false), [onBusy]);

  async function handle(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;
    setError(null);
    const list = [...files];
    setBusy((n) => n + list.length);
    // Nacheinander, damit ältere Handys nicht am Speicher scheitern.
    for (const file of list) {
      try {
        await store.addPhoto(props.roomId, props.step, await processImage(file), props.section);
        props.onAdded?.();
      } catch (e) {
        console.error(e);
        setError("Ein Foto konnte nicht gelesen werden. Bitte noch einmal aufnehmen.");
      }
      setBusy((n) => n - 1);
    }
    if (camera.current) camera.current.value = "";
    if (gallery.current) gallery.current.value = "";
    // Nach der Rückkehr aus der Kamera behielt das Feld den Fokus; der nächste
    // Tipp auf "Foto aufnehmen" kam dann nicht an (Rückmeldung 08.10.2026).
    (document.activeElement as HTMLElement | null)?.blur?.();
  }

  // Demo-Weg: gezeichnete Beispielfotos passend zum Raumtyp, reihum.
  // Flur (14.10.2026): ein gezeichneter Flur mit einer Ecke nach rechts, je Fotoart und Abschnitt das passende Bild
  // (website/raumbilder.mjs). Diele und ältere Flurfotos: die Wandbilder bzw. die alten Bilder reihum.
  async function beispiel() {
    const room = d.rooms.find((r) => r.id === props.roomId);
    const flurFoto = (HALL_PHOTO_STEPS as readonly string[]).includes(props.step) || (room?.type === "flur" && props.step !== "lampe");
    const zweiter = (props.section ?? 1) > 1;
    const wieOft = (step: PhotoStep) => d.photos.filter((p) => p.roomId === props.roomId && p.step === step).length;
    const FLUR: Partial<Record<PhotoStep, string>> = {
      tuer: "flur-eingang",
      flur_knick: "flur-ecke",
      flur_links: zweiter ? "flur-links-2" : "flur-links-1",
      flur_rechts: zweiter ? "flur-rechts-2" : "flur-rechts-1",
      flur_zurueck: "flur-zurueck",
    };
    const ringsum = ["flur-links-1", "flur-rechts-1", "flur-links-2", "flur-rechts-2"];
    const art = room?.type === "kueche" ? "kueche" : room?.type === "wohnen" || room?.type === "esszimmer" ? "wohnzimmer" : "schlafzimmer";
    // Räume nach Wänden (15.10.2026): je Wand das passende Bild; Bad und Balkon in der Kurzform (hinein, zurück).
    const WAND: Partial<Record<PhotoStep, string>> = { wand_gegenueber: "gegenueber", wand_rechts: "rechts", wand_links: "links", wand_tuer: "tuer", wand_nische: "gegenueber" };
    const klein = room?.type === "balkon" ? "balkon" : "bad";
    const name = flurFoto
      ? (FLUR[props.step] ?? (props.step === "rundum" ? ringsum[wieOft("rundum") % ringsum.length]! : `flur-${(wieOft(props.step) % 2) + 1}`))
      : WAND[props.step]
        ? `${art}-${WAND[props.step]}`
        : props.step === "lampe"
          ? ["lampe-plafonniere", "lampe-pendel", "lampe-kronleuchter"][wieOft("lampe") % 3]!
          : props.step === "klein_rein" || props.step === "klein_zurueck"
          ? `${klein}-${props.step === "klein_rein" ? "rein" : "zurueck"}`
          : `${art}-${(wieOft(props.step) % 3) + 1}`;
    const blob = await (await fetch(`/demo-fotos/${name}.jpg`)).blob();
    await handle([new File([blob], `${name}.jpg`, { type: "image/jpeg" })]);
  }

  // <label for> statt click() per Skript: Den öffnet der Browser selbst, auch
  // direkt nach der Rückkehr aus der Kamera-App (kein "toter" erster Tipp).
  if (props.kompakt)
    return (
      <div className="kamera kompakt">
        <input id={`${id}-k`} ref={camera} className="datei" type="file" accept="image/*" capture="environment" disabled={busy > 0} onChange={(e) => void handle(e.target.files)} />
        <input id={`${id}-g`} ref={gallery} className="datei" type="file" accept="image/*" multiple={!props.single} disabled={busy > 0} onChange={(e) => void handle(e.target.files)} />
        <label htmlFor={`${id}-k`} className={`kachel-kamera${busy > 0 ? " is-disabled" : ""}`} role="button" aria-disabled={busy > 0} aria-label={props.label ?? "Foto aufnehmen"}>
          {busy > 0 ? "…" : "📷"}
          <span>{busy > 0 ? "lädt" : "Foto"}</span>
        </label>
        <label htmlFor={`${id}-g`} className="kachel-galerie" role="button">
          Galerie
        </label>
        {d.company.demo && (
          <button type="button" className="kachel-galerie" disabled={busy > 0} onClick={() => void beispiel()}>
            Beispielfoto
          </button>
        )}
        {error && <p className="error">{error}</p>}
      </div>
    );
  return (
    <div className="kamera">
      <input id={`${id}-k`} ref={camera} className="datei" type="file" accept="image/*" capture="environment" disabled={busy > 0} onChange={(e) => void handle(e.target.files)} />
      <input id={`${id}-g`} ref={gallery} className="datei" type="file" accept="image/*" multiple={!props.single} disabled={busy > 0} onChange={(e) => void handle(e.target.files)} />
      {props.step !== "schrank_offen" && props.zoomHint !== false && (
        <p className="hint zoom">Tipp: Stellen Sie die Kamera auf <strong>0,5×</strong> (Weitwinkel), dann passt die ganze Wand aufs Bild.</p>
      )}
      <label htmlFor={`${id}-k`} className={`btn btn-primary btn-wide btn-camera${busy > 0 ? " is-disabled" : ""}`} role="button" aria-disabled={busy > 0}>
        {busy > 0 ? `Foto wird vorbereitet … (${busy})` : (props.label ?? "📷 Foto aufnehmen")}
      </label>
      <label htmlFor={`${id}-g`} className={`btn btn-quiet btn-wide${busy > 0 ? " is-disabled" : ""}`} role="button" aria-disabled={busy > 0}>
        Aus der Galerie wählen
      </label>
      {d.company.demo && (
        <button type="button" className="btn btn-secondary btn-wide" disabled={busy > 0} onClick={() => void beispiel()}>
          Beispielfoto verwenden (Demo)
        </button>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
